// SmartLoackService.jsx


import axios from "axios";

// Axios instance
const apiClient = axios.create({
  // baseURL: "https://ai-realtor-seam.onrender.com/api",
  baseURL: `${process.env.REACT_APP_SEAM_SERVER_URL}/api`,

  headers: {
    "Content-Type": "application/json",
  },
});

/**
 * Fetch smart locks associated with the user.
 * @param {string} userId - UID of the authenticated user.
 * @returns {Promise<Array>} - List of smart locks.
 * @throws {Error} - If the request fails.
 */
export const fetchSmartLocks = async (userId) => {
  if (!userId) {
    throw new Error("User ID is required to fetch smart locks.");
  }

  try {
    const response = await apiClient.get("/user-locks", {
      params: { uid: userId },
    });
    return response.data.devices;
  } catch (error) {
    console.error(
      `Error fetching smart locks for user ${userId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Create a Seam Connect Webview.
 * @param {Object} params - The parameters for creating the Connect Webview.
 * @param {string} params.uid - The authenticated user's UID.
 * @param {string} params.redirectUrl - The success redirect URL.
 * @param {string} params.failureUrl - The failure redirect URL.
 * @returns {Promise<string>} - The URL for the Seam Connect Webview.
 * @throws {Error} - If the request fails.
 */
export const createConnectWebview = async ({ uid, redirectUrl, failureUrl }) => {
  try {
    const response = await apiClient.post("/connect-webview", {
      uid,
      redirectUrl,
      failureUrl,
    });
    return response.data.webviewData.url;
  } catch (error) {
    console.error(
      "Error creating Seam Connect Webview:",
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Lock a smart lock.
 * @param {string} lockId - The ID of the lock to be locked.
 * @param {string} userId - The authenticated user's UID.
 * @returns {Promise<object>} - Lock action response.
 * @throws {Error} - If the request fails.
 */
export const lockSmartLock = async (lockId, userId) => {
  if (!lockId || !userId) {
    throw new Error("Both lockId and userId are required to lock the smart lock.");
  }

  try {
    const response = await apiClient.post("/lock-device", {
      lockId,
      uid: userId,
    });
    return response.data;
  } catch (error) {
    console.error(
      `Error locking smart lock ${lockId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Unlock a smart lock.
 * @param {string} lockId - The ID of the lock to be unlocked.
 * @param {string} userId - The authenticated user's UID.
 * @returns {Promise<object>} - Unlock action response.
 * @throws {Error} - If the request fails.
 */
export const unlockSmartLock = async (lockId, userId) => {
  if (!lockId || !userId) {
    throw new Error("Both lockId and userId are required to unlock the smart lock.");
  }

  try {
    const response = await apiClient.post("/unlock-device", {
      lockId,
      uid: userId,
    });
    return response.data;
  } catch (error) {
    console.error(
      `Error unlocking smart lock ${lockId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Check the status of a lock or unlock action.
 * @param {string} actionAttemptId - The ID of the action attempt.
 * @returns {Promise<object>} - Action attempt status response.
 * @throws {Error} - If the request fails.
 */
export const checkActionStatus = async (actionAttemptId) => {
  if (!actionAttemptId) {
    throw new Error("Action attempt ID is required to check action status.");
  }

  try {
    const response = await apiClient.get("/action-status", {
      params: { actionAttemptId },
    });
    return response.data.statusResponse;
  } catch (error) {
    console.error(
      `Error checking status for action attempt ${actionAttemptId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Get the locking status of a specific smart lock.
 * @param {string} lockId - The ID of the lock to check status.
 * @returns {Promise<object>} - Lock status and properties.
 * @throws {Error} - If the request fails.
 */
export const getLockStatus = async (lockId) => {
  if (!lockId) {
    throw new Error("Lock ID is required to check lock status.");
  }

  try {
    const response = await apiClient.get("/lock-status", {
      params: { lockId },
    });
    return response.data.lockStatus;
  } catch (error) {
    console.error(
      `Error fetching lock status for ${lockId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Create an access code for a smart lock with optional start/expiration times.
 * @param {string} userId - The authenticated user's UID.
 * @param {string} deviceId - The ID of the lock for which the code is created.
 * @param {string} name - The name of the access code.
 * @param {string|null} [code] - The optional access code to set (backend generates a random code if not provided).
 * @param {string} [startsAt] - The optional start time for the access code (ISO format).
 * @param {string} [endsAt] - The optional expiration time for the access code (ISO format).
 * @param {boolean} [isOneTimeUse] - Whether the access code is single-use (default: false).
 * @param {boolean} [isOfflineAccessCode] - Whether the access code is offline (default: false).
 * @returns {Promise<object>} - Response after creating the access code.
 * @throws {Error} - If the request fails.
 */
export const createAccessCode = async (
  userId,
  deviceId,
  name,
  code,
  startsAt,
  endsAt,
  isOneTimeUse = false,
  isOfflineAccessCode = false,
  tenantId = null,
  propertyId = null,
  options = {}
) => {
  if (!userId || !deviceId || !name) {
    throw new Error("UserId, deviceId, and name are required to create an access code.");
  }
  if (code === "") {
    throw new Error("Access code cannot be an empty string.");
  }

  try {
    const response = await apiClient.post("/access-codes/create", {
      uid: userId,
      deviceId,
      name,
      code: code || undefined,
      startsAt: startsAt || undefined,
      endsAt: endsAt || undefined,
      isOneTimeUse,
      isOfflineAccessCode,

      tenantId: tenantId || undefined,
      propertyId: propertyId || undefined,

      isGuest: options?.isGuest ?? true,
      isPermanentTenantCode:
        options?.isPermanentTenantCode ?? false,
    });

    return response.data;
  } catch (error) {
    console.error(
      `Error creating access code for device ${deviceId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Fetch all access codes for a specific smart lock.
 * @param {string} userId - The authenticated user's UID.
 * @param {string} deviceId - The ID of the lock.
 * @returns {Promise<Array>} - List of access codes.
 * @throws {Error} - If the request fails.
 */
export const fetchAccessCodes = async (userId, deviceId) => {
  if (!userId || !deviceId) {
    throw new Error("Both userId and deviceId are required to fetch access codes.");
  }

  try {
    const response = await apiClient.get("/access-codes", {
      params: { uid: userId, deviceId },
    });
    console.log("Access Codes Response:", response.data);
    return response.data.accessCodes.access_codes;
  } catch (error) {
    console.error(
      `Error fetching access codes for device ${deviceId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Delete an access code for a smart lock.
 * @param {string} userId - The authenticated user's UID.
 * @param {string} accessCodeId - The ID of the access code to delete.
 * @param {string} deviceId - The ID of the lock (required for backend).
 * @returns {Promise<object>} - Response after deleting the access code.
 * @throws {Error} - If the request fails.
 */
export const deleteAccessCode = async (userId, accessCodeId, deviceId) => {
  if (!userId || !accessCodeId || !deviceId) {
    throw new Error("Both userId and accessCodeId are required to delete an access code.");
  }

  try {
    const response = await apiClient.delete("/access-codes/delete", {
      data: {
        uid: userId,
        accessCodeId,
        deviceId,
      },
    });
    return response.data;
  } catch (error) {
    console.error(
      `Error deleting access code ${accessCodeId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};

/**
 * Update the name of a smart lock.
 * @param {string} userId - The authenticated user's UID.
 * @param {string} deviceId - The ID of the lock to be updated.
 * @param {string} newName - The new name for the lock.
 * @returns {Promise<object>} - Response after updating the lock name.
 * @throws {Error} - If the request fails.
 */
export const updateLockName = async (userId, deviceId, newName) => {
  if (!userId || !deviceId || !newName) {
    throw new Error("All fields (userId, deviceId, newName) are required to update the lock name.");
  }

  try {
    const response = await apiClient.post("/update-lock-name", {
      uid: userId,
      deviceId,
      newName,
    });
    console.log(`Lock ${deviceId} name updated to "${newName}" successfully.`);
    return response.data;
  } catch (error) {
    console.error(
      `Error updating lock name for device ${deviceId}:`,
      error.response?.data || error.message
    );
    throw error;
  }
};
